import type {CommentSubjectTypes} from '@github-ui/commenting/CommentHeader'
import type {RefObject} from 'react'
import {useEffect} from 'react'

import {useInlineCommentDialogModeContext} from '../contexts/InlineCommentDialogModeContext'
import type {Thread} from '../types'
import {hideInteractiveElements, showInteractiveElements} from '../util/hide-show-interactive-elements'

export function useInlineCommentDialogModeInteractiveElements({
  commentSubjectType,
  markerRef,
  isThread = false,
  thread,
}: {
  commentSubjectType: CommentSubjectTypes | undefined // TODO: why is this ever undefined?
  markerRef: RefObject<HTMLElement | null>
  isThread?: boolean // Used to determine if the marker is a thread or an annotation.
  thread?: Thread | undefined // Only relevant if `isThread` is true.
}) {
  const {isInDialogMode} = useInlineCommentDialogModeContext()

  useEffect(() => {
    let timeout = null

    // `thread` and `isThread` can be removed from the dependencies once
    // https://github.com/github/pull-requests/issues/18119 is resolved.
    // Annotations are included in the payload, but threads are loaded in async.
    // Therefore, when dealing with threads, we have to ensure the thread is available
    // before we try to manipulate the DOM.
    if (isThread && !thread) return

    if (!isInDialogMode) {
      hideInteractiveElements(markerRef.current)
    } else if (commentSubjectType === 'commit') {
      // This delay works around a race condition between loading and rendering
      // the comment and attempting to manipulate the DOM.
      // This only affects the commit detail page, and should be removed when
      // the commit detail page is updated to work more like the PR files
      // changed page.
      timeout = setTimeout(() => {
        showInteractiveElements(markerRef.current)
      }, 100)
    } else {
      showInteractiveElements(markerRef.current)
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout)
      }
    }
  }, [isInDialogMode, commentSubjectType, markerRef, isThread, thread])
}
